"use client";

import { useEffect } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { SEASON_CORRECT_SUFFIX } from "@/lib/season-skin";

/**
 * 정답 피드백 토스트 — ProblemAnswer 제출 시 `answerFeedback` CustomEvent 를 받아
 * sonner toast 로 정답/오답 결과를 표시한다 (JCG-2096, JCG-2112).
 *
 * 이전에는 레이아웃별로 표시 로직이 분산돼 있었다(셸: 자체 fixed 토스트, 클래식/모바일:
 * StickyTitle 의 정답 피드백 분기). 이제 레이아웃과 무관하게 이 리스너 하나가 sonner toast
 * 단일 경로로 일원화한다. 실제 렌더는 layout.tsx 의 <SonnerToaster> 가 담당하므로 이 컴포넌트는
 * DOM 을 그리지 않고 이벤트 → toast 변환만 수행한다.
 */
export default function AnswerFeedbackToast() {
  useEffect(() => {
    const handleAnswerFeedback = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { isCorrect: boolean; isVisible: boolean }
        | undefined;
      if (!detail?.isVisible) return;

      const { isCorrect } = detail;
      toast.custom(
        () => (
          <div
            className={`flex items-center gap-2 rounded-full border px-4 py-2 shadow-lg backdrop-blur-sm ${
              isCorrect
                ? "border-green-300 bg-green-50/90 text-green-600 dark:border-green-800 dark:bg-green-950/80 dark:text-green-300"
                : "border-red-300 bg-red-50/90 text-red-600 dark:border-red-800 dark:bg-red-950/80 dark:text-red-300"
            }`}
            role="status"
            aria-live="polite"
          >
            {isCorrect ? (
              <CheckCircle2 className="h-6 w-6 md:h-7 md:w-7" />
            ) : (
              <XCircle className="h-6 w-6 md:h-7 md:w-7" />
            )}
            <span className="text-sm font-medium md:text-lg">
              {isCorrect ? "정답입니다!" : "틀렸습니다"}
              {/* 시즌 스킨(JCG-3694) 장식 */}
              {isCorrect && SEASON_CORRECT_SUFFIX && (
                <span aria-hidden="true" className="hidden season:inline">
                  {" "}
                  {SEASON_CORRECT_SUFFIX}
                </span>
              )}
            </span>
          </div>
        ),
        // 고정 id 로 빠른 연속 제출 시 토스트가 쌓이지 않고 단일 토스트로 갱신되게 한다
        // (기존 셸 전용 토스트의 "항상 1개만 갱신" 동작 보존, JCG-2112).
        { id: "answer-feedback", duration: 3000 }
      );
    };

    window.addEventListener("answerFeedback", handleAnswerFeedback);
    return () => {
      window.removeEventListener("answerFeedback", handleAnswerFeedback);
    };
  }, []);

  return null;
}
